import React from 'react';
import styled from 'styled-components';
import { TbMail, TbMapPin, TbBrandGithub, TbBrandTwitter, TbBrandLinkedin } from 'react-icons/tb';
import { useTheme } from '@contexts';
import { LogoIcon } from '../../atoms';

const StyledFooter = styled.footer`
  background-color: transparent;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  padding: 4rem 0 2rem;
  position: relative;
  z-index: 10;
  width: 100%;
  overflow: visible;
  
  @media (max-width: 768px) {
    padding: 3rem 0 2rem;
  }
`;

const FooterContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 3rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    padding: 0 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 2rem;
  margin-bottom: 2rem;
  min-width: 0;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1.5rem;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FooterLogoIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FooterDescription = styled.p`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const FooterTitle = styled.h3`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.letterSpacing.wide};
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  transition: color ${({ theme }) => theme.duration.fast}ms ${({ theme }) => theme.easing.easeOut};
  line-height: 1.4;
  word-break: break-word;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary.main};
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    gap: 0.375rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.background.paper};
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.duration.fast}ms ${({ theme }) => theme.easing.easeOut};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.main};
    color: white;
    transform: translateY(-2px);
  }
`;

const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  min-width: 0;
  
  @media (max-width: 768px) {
    padding-top: 1.5rem;
    gap: 0.5rem;
  }
`;

const Copyright = styled.p`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConfidentialNotice = styled.p`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.tertiary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Footer = () => {
  const { theme } = useTheme();

  return (
    <StyledFooter>
      <FooterContainer>
        <FooterContent>
          <FooterSection>
            <FooterLogo>
              <FooterLogoIconWrapper>
                <LogoIcon />
              </FooterLogoIconWrapper>
              <span>SenseAI</span>
            </FooterLogo>
            <FooterDescription>
              Privacy-first contactless sensing technology empowering longevity with dignity.
              World's first thermal array sensor for anonymous human sensing.
            </FooterDescription>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Contact</FooterTitle>
            <ContactItem href="mailto:contact@sense-ai.org">
              <TbMail size={16} />
              contact@sense-ai.org
            </ContactItem>
            <ContactItem href="#">
              <TbMapPin size={16} />
              Hong Kong
            </ContactItem>
            <SocialLinks>
              <SocialLink href="#" aria-label="GitHub">
                <TbBrandGithub size={18} />
              </SocialLink>
              <SocialLink href="#" aria-label="Twitter">
                <TbBrandTwitter size={18} />
              </SocialLink>
              <SocialLink href="#" aria-label="LinkedIn">
                <TbBrandLinkedin size={18} />
              </SocialLink>
            </SocialLinks>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>© 2026 SenseAI. All rights reserved.</Copyright>
          <ConfidentialNotice>Confidential & Proprietary</ConfidentialNotice>
        </FooterBottom>
      </FooterContainer>
    </StyledFooter>
  );
};

export default Footer;
